/**
 * www Coach chat: keep the last bubble clear of the docked composer.
 * Collapsed composer is input (44) + context row (44) + dock padding.
 * At Scout 390, the transcript is the only vertical scroll owner.
 */

import { WWW_PHONE_WIDTH_PX } from './wwwPhoneClipLayout';

export const COACH_PHONE_WIDTH_PX = WWW_PHONE_WIDTH_PX;
export const COACH_COMPOSER_DOCK_MIN_PX = 160;
export const COACH_TRANSCRIPT_TESTID = 'coach-transcript';
export const COACH_COMPOSER_DOCK_TESTID = 'coach-composer-dock';
export const COACH_LATEST_BUBBLE_TESTID = 'coach-transcript-latest-bubble';
export const COACH_PHONE_CHATS_TESTID = 'coach-phone-chats';

export const COACH_ROUTE_PATH = '/coach';

/** App shell: lock `#main-content` so only the transcript scrolls at Scout 390. */
export const COACH_LAYOUT_MAIN_LOCK_CLASS =
  'max-lg:overflow-hidden max-lg:flex max-lg:flex-col';

export const COACH_LAYOUT_BODY_LOCK_CLASS =
  'max-lg:flex-1 max-lg:min-h-0 max-lg:flex max-lg:flex-col max-lg:pb-0';

export const COACH_LAYOUT_ITEM_LOCK_CLASS =
  'max-lg:flex-1 max-lg:min-h-0 max-lg:flex max-lg:flex-col';

/**
 * Fill leftover Layout column at Scout 390. Do not use a 72dvh island here -
 * that plus the chats rail is the nested page+transcript miss.
 */
export const COACH_PHONE_STAGE_CLASS =
  'max-lg:flex max-lg:flex-1 max-lg:min-h-0 max-lg:flex-col max-lg:overflow-hidden';

export function isCoachRoute(pathname) {
  return pathname === COACH_ROUTE_PATH;
}

export const COACH_PHONE_STAGE_SPACING_CLASS = 'space-y-4 max-lg:space-y-2';

export const COACH_WORKSPACE_CLASS =
  'grid grid-cols-1 items-start lg:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] gap-4 lg:gap-6 max-lg:min-h-0 max-lg:flex-1 max-lg:items-stretch max-lg:grid-rows-[auto_minmax(0,1fr)]';

export const COACH_CONVERSATION_PANE_CLASS =
  'instrument-fuel-tile relative isolate flex flex-col min-h-[280px] min-w-0 !p-0 overflow-hidden max-lg:h-auto max-lg:min-h-0 max-lg:flex-1 lg:h-[min(72dvh,680px)]';

export const COACH_TRANSCRIPT_SCROLL_CLASS =
  'flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 pt-4 sm:px-5 sm:pt-5 space-y-4 min-h-0 min-w-0 [overflow-anchor:none]';

export const COACH_CHATS_DESKTOP_LIST_CLASS =
  'hidden lg:block overflow-y-auto space-y-1 max-h-[min(54vh,460px)]';

export const COACH_CHATS_PHONE_CLASS = 'lg:hidden';

export const COACH_COMPOSER_DOCK_CLASS =
  'absolute bottom-0 inset-x-0 z-10 shrink-0 p-4 bg-[var(--bg-card)] shadow-[var(--shadow-panel)]';

export function isCoachPhoneViewport(widthPx) {
  return Number(widthPx) <= COACH_PHONE_WIDTH_PX;
}

/**
 * At Scout 390 the page and chats rail must not independently scroll.
 * Desktop keeps the chats rail + page as additional scroll regions.
 */
export function resolveCoachScrollOwners(widthPx) {
  if (isCoachPhoneViewport(widthPx)) {
    return Object.freeze({ page: false, chats: false, transcript: true });
  }
  return Object.freeze({ page: true, chats: true, transcript: true });
}

export function resolveCoachTranscriptEndPad(composerHeightPx) {
  const measured = Number(composerHeightPx);
  const height = Number.isFinite(measured) ? Math.ceil(measured) : 0;
  return Math.max(COACH_COMPOSER_DOCK_MIN_PX, height);
}

const DEFAULT_NAV_PX = 56;
const DEFAULT_MAIN_PAD_BOTTOM_PX = 24;

/**
 * Viewport math for Scout 390: conversation fills leftover stage height
 * instead of stacking 54vh chats + 72dvh transcript (page+transcript fight).
 */
export function measureCoachPhoneTranscriptLayout({
  viewportHeightPx,
  navPx = DEFAULT_NAV_PX,
  mainPadBottomPx = DEFAULT_MAIN_PAD_BOTTOM_PX,
  headerPx,
  chatsPx,
  composerHeightPx,
  lastBubbleHeightPx,
}) {
  const stageHeight = Math.max(0, Number(viewportHeightPx) - Number(navPx) - Number(mainPadBottomPx));
  const conversationHeight = Math.max(0, stageHeight - Number(headerPx) - Number(chatsPx));
  const pad = resolveCoachTranscriptEndPad(composerHeightPx);
  const composerHeight = Math.max(0, Number(composerHeightPx) || 0);
  const composerTop = Math.max(0, conversationHeight - composerHeight);
  const lastBubbleBottom = Math.max(0, conversationHeight - pad);
  const lastBubbleTop = lastBubbleBottom - Number(lastBubbleHeightPx || 0);
  const pageContentHeight = Number(navPx) + Number(mainPadBottomPx) + stageHeight;

  return {
    stageHeight,
    conversationHeight,
    pad,
    composerTop,
    lastBubbleTop,
    lastBubbleBottom,
    lastBubbleClearsComposer: lastBubbleBottom <= composerTop,
    deadGapAboveComposerPx: Math.max(0, composerTop - lastBubbleBottom),
    pageScrolls: pageContentHeight > Number(viewportHeightPx),
    scrollOwners: Object.freeze(['transcript']),
  };
}

/**
 * Stacked 54vh chats + 72dvh pane at phone width (the live 390 miss).
 */
export function measureLegacyNestedCoachLayout({
  viewportHeightPx,
  headerPx,
  chatsMaxVh = 0.54,
  conversationVh = 0.72,
}) {
  const chatsPx = Number(viewportHeightPx) * chatsMaxVh;
  const conversationHeight = Number(viewportHeightPx) * conversationVh;
  const pageContentHeight = Number(headerPx) + chatsPx + conversationHeight;
  return {
    chatsPx,
    conversationHeight,
    pageContentHeight,
    pageScrolls: pageContentHeight > Number(viewportHeightPx),
    scrollOwners: Object.freeze(['page', 'chats', 'transcript']),
  };
}

/**
 * Scroll the newest bubble into the transcript viewport, leaving the docked
 * composer overlay clear. `scrollIntoView({ block: 'end' })` would hide the
 * bubble under the absolute composer; padding from resolveCoachTranscriptEndPad
 * is the reserved bottom band.
 */
export function scrollCoachLastBubbleAboveComposer(transcriptEl, lastBubbleEl, composerHeightPx) {
  if (!transcriptEl || typeof transcriptEl.scrollTop !== 'number') return;

  const pad = resolveCoachTranscriptEndPad(composerHeightPx);
  const clientHeight = Number(transcriptEl.clientHeight);
  const bubbleTop = Number(lastBubbleEl?.offsetTop);
  const bubbleHeight = Number(lastBubbleEl?.offsetHeight);

  if (Number.isFinite(clientHeight) && Number.isFinite(bubbleTop) && Number.isFinite(bubbleHeight)) {
    const visible = Math.max(0, clientHeight - pad);
    if (bubbleHeight > visible) {
      transcriptEl.scrollTop = Math.max(0, bubbleTop);
      return;
    }
    const bubbleBottom = bubbleTop + bubbleHeight;
    transcriptEl.scrollTop = Math.max(0, bubbleBottom - clientHeight + pad);
    return;
  }

  const scrollHeight = Number(transcriptEl.scrollHeight);
  if (Number.isFinite(scrollHeight) && Number.isFinite(clientHeight)) {
    transcriptEl.scrollTop = Math.max(0, scrollHeight - clientHeight);
  }
}
